import { createContext, useContext, useEffect, useState } from "react";
import api, { tokenStore } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);
const MODE_KEY = "rl_view_mode";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous
  const [loading, setLoading] = useState(true);
  // viewMode: "coach" | "student". Only meaningful for dual-role users.
  // Defaults to "coach". On a fresh login it resets to "coach" (user's primary lens).
  // Survives page refreshes within a session via sessionStorage.
  const [viewMode, setViewModeState] = useState(() => {
    try { return window.sessionStorage.getItem(MODE_KEY) || "coach"; }
    catch { return "coach"; }
  });

  const setViewMode = (next) => {
    const v = next === "student" ? "student" : "coach";
    setViewModeState(v);
    try { window.sessionStorage.setItem(MODE_KEY, v); } catch { /* ignore */ }
  };

  const resetViewMode = () => {
    setViewModeState("coach");
    try { window.sessionStorage.removeItem(MODE_KEY); } catch { /* ignore */ }
  };

  useEffect(() => {
    let cancelled = false;
    // Iter94e — Only treat 401 as "logged out". Transient failures (network
    // blips, 5xx, Safari suspending JS on backgrounding) previously wiped the
    // user to `false`, which sent students to /login even though their tokens
    // were still valid. Retry once with a 1.5s backoff to ride out short
    // outages before conceding.
    // Iter94f — track offline→online transitions so we can toast "You're back
    // online" when the app auto-recovers from a `null` (Reconnecting…) state.
    const wasOffline = { current: false };
    const fetchMe = async (attempt = 0) => {
      // Iter244 — Skip /auth/me when there is no stored bearer token AND
      // no refresh token. Public pages (festival self-service, viewer,
      // helper redeem, landing) otherwise fire a guaranteed 401 on mount
      // + another on the interceptor's refresh retry, cluttering the
      // guest console with 4-6 red errors. If tokens appear later (e.g.
      // login), setUser is called explicitly by the login() flow.
      if (!tokenStore.getAccess() && !tokenStore.getRefresh()) {
        if (!cancelled) {
          setUser(false);
          setLoading(false);
        }
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        setUser(data);
        if (wasOffline.current) {
          // Only toast when we actually recovered from a broken state (never
          // on first successful mount) so we don't yell at every login.
          toast.success("You're back online", { duration: 2500 });
          wasOffline.current = false;
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          setUser(false);
          return;
        }
        if (attempt < 1) {
          setTimeout(() => fetchMe(attempt + 1), 1500);
          return;
        }
        // After a retry we still can't reach the backend — keep the user in
        // an "unknown" state (null) rather than false so ProtectedRoute waits
        // instead of redirecting away from their intended page. Any stored
        // tokens survive so a later successful request re-hydrates them.
        wasOffline.current = true;
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMe();
    // Iter94e — when Safari brings the tab back into the foreground after
    // suspending JS (backgrounding, opening another tab, iOS multitasking),
    // re-fetch /auth/me so a `null` (unknown) user gets re-hydrated to their
    // real profile instead of the app staying stuck in "Reconnecting…".
    const onVisible = () => { if (document.visibilityState === "visible") fetchMe(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  const login = async (email, password, _remember = true) => {
    const remember = true;
    const { data } = await api.post("/auth/login", { email, password, remember });
    // Persist BOTH tokens — bearer-header path keeps "Remember me" working even when
    // Safari ITP / Chrome's third-party cookie phase-out blocks our auth cookies.
    tokenStore.store(data.access_token, data.refresh_token, remember);
    // Re-fetch /auth/me so the user object includes all derived flags (dual_role_enabled,
    // tracking_active, assessment_required, etc.) that the login endpoint doesn't return.
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {
      setUser(data);
    }
    resetViewMode(); // Always land in COACH mode on a fresh login.
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // If the email was already taken, backend silently sends a magic-login
    // link and returns {email_exists_magic_sent:true}. No session is issued
    // (we don't want to let a stranger typing someone else's email in).
    if (data.email_exists_magic_sent) return data;
    // New signups always default to "remember me" so they don't get bounced after 8 hours.
    tokenStore.store(data.access_token, data.refresh_token, true);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {
      setUser(data);
    }
    resetViewMode();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    tokenStore.clear();
    resetViewMode();
    setUser(false);
  };

  const refresh = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch {
      return null;
    }
  };

  // Derived: when a non-dual-role user is loaded, force viewMode back to "coach"
  // so the UI doesn't get stuck in a stale "student" mode from a previous session.
  useEffect(() => {
    if (user && !user.dual_role_enabled && viewMode !== "coach") {
      resetViewMode();
    }
  }, [user]);  

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, refresh, viewMode, setViewMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
